import { Avatar, Divider, Grid, Stack, Typography } from '@mui/material';
import { ILongtermEquipmentCard } from 'API/LongtermEquipment';
import { requestFile } from 'API/medicalReferrals';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';
import { shortDateFormat } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { Download2, ExternalLink, LeftArrow } from 'Images';
import { downloadFile } from 'Utils';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Label } from './Label';

interface IRow {
  data: ILongtermEquipmentCard;
}

export const Row = ({ data }: IRow) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.LongtermEquipment);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const scaleLoader = isDesktop ? 'scale(0.5)' : 'scale(0.25)';
  const { isLoading, loader, setIsLoading } = useLoader(
    scaleLoader,
    undefined,
    undefined,
    false
  );

  const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setIsLoading(true);
    e.stopPropagation();
    let file;
    if (data.medicalReferralId) {
      file = await requestFile(data.medicalReferralId);
    }
    setIsLoading(false);
    if (file && file.fileContentBase64) {
      downloadFile(
        file.fileContentBase64,
        'pdf',
        file.documentName || 'New Document'
      );
    }
    return;
  };

  const navigateToAdditionalInfoLink = () => {
    data.additionalInfoLink && window.open(data.additionalInfoLink, '_blank');
  };

  return isDesktop ? (
    <Grid
      container
      sx={{
        borderBottom: '1px solid',
        borderColor: 'primary.shade_100',
        bgcolor: '#fff',
        borderRadius: '16px',
        alignItems: 'center',
      }}
    >
      <Stack direction='row' spacing={2} width={'30%'} minHeight={'200px'}>
        <Stack
          direction='row'
          spacing={2}
          sx={{
            width: '240px',
            backgroundColor: data.isAvailable ? '#E0F6EF' : '#FFEDCE',
            padding: '1rem',
            borderRadius: '16px 0 0 16px',
          }}
        >
          <Stack sx={{ p: '24px 0 32px' }}>
            <Typography component={'h2'}>{data.description}</Typography>
            {data.isAvailable && <Label text={data.filterLabel} />}
            {data.validToDate && !data.isPermanent && (
              <Stack gap={1} mt={1}>
                <Typography variant='body6'>
                  {dictionary.endOfExercisedPeriod}
                </Typography>
                <Typography variant='body2'>
                  {format(new Date(data.validToDate), shortDateFormat)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing={2} width={'70%'} sx={{ p: 3 }}>
        <Stack direction='row' spacing={2} justifyContent={'space-between'}>
          <Stack sx={{ maxWidth: '33%' }}>
            <Typography variant='body6'>{dictionary.exercisedFrom}</Typography>
            <Typography variant='body2'>
              {format(new Date(data.nextEntitleDate), shortDateFormat)}
            </Typography>
          </Stack>
          {data.lastRefStartDate && (
            <Stack>
              <Typography variant='body6'>
                {dictionary.previousExercised}
              </Typography>
              <Typography variant='body2'>
                {format(new Date(data.lastRefStartDate), shortDateFormat)}
              </Typography>
            </Stack>
          )}
          {isLoading ? (
            <Stack
              height={'40px'}
              justifyContent={'center'}
              alignItems={'end'}
              width={'33%'}
            >
              {loader}
            </Stack>
          ) : (
            data.medicalReferralId && (
              <Stack
                flexDirection={'row'}
                mb={1}
                alignItems={'center'}
                sx={{
                  cursor: 'pointer',
                  maxWidth: '33%',
                  whiteSpace: 'nowrap',
                }}
                component={'a'}
                tabIndex={0}
                onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                  download(e)
                }
              >
                <Avatar
                  src={Download2}
                  alt={''}
                  sx={{
                    width: '20px',
                    height: '20px',
                    mr: 1,
                  }}
                />
                <Typography variant='body5' color='primary.main'>
                  {dictionary.downloadPreviousReference}
                </Typography>
              </Stack>
            )
          )}
        </Stack>
        <Divider
          aria-hidden={'true'}
          flexItem
          sx={{
            borderColor: 'primary.shade_100',
          }}
        />
        <Stack pt={1}>
          <OneOrTwoButtons
            width={isDesktop ? 'fit-content' : '100%'}
            leftButton={
              data.filterLabel
                ? {
                    label: dictionary.requestReference,
                    icon: LeftArrow,
                    onClick: () =>
                      data.shikumSubTypeId !== undefined
                        ? navigate(
                            `/universalRequest/1/2/${data.shikumSubTypeId}`
                          )
                        : navigate('/universalRequest/1/2'),
                  }
                : undefined
            }
            rightButton={
              data.additionalInfoLink
                ? {
                    label: dictionary.moreInfo,
                    ariaLabel: `${dictionary.moreInfoAbout} ${data.description}`,
                    icon: ExternalLink,
                    onClick: () => {
                      navigateToAdditionalInfoLink();
                    },
                  }
                : undefined
            }
          />
        </Stack>
      </Stack>
    </Grid>
  ) : (
    <>
      <Grid
        container
        sx={{
          borderBottom: '1px solid',
          borderColor: 'primary.shade_100',
          bgcolor: '#fff',
          borderRadius: '16px',
          alignItems: 'center',
        }}
        tabIndex={0}
      >
        <Stack direction='column' spacing={2}>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              width: '100%',
              backgroundColor: data.isAvailable ? '#E0F6EF' : '#FFEDCE',
              padding: '1rem',
              borderRadius: '16px 16px 0 0',
            }}
          >
            <Stack sx={{ p: '24px 0 0', ml: '0!important' }}>
              <Typography component={'h2'}>{data.description}</Typography>
              {data.isAvailable && <Label text={data.filterLabel} />}
              {data.validToDate && !data.isPermanent && (
                <Stack gap={1} mt={1}>
                  <Typography variant='body6'>
                    {dictionary.endOfExercisedPeriod}
                  </Typography>
                  <Typography variant='body2'>
                    {format(new Date(data.validToDate), shortDateFormat)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Stack spacing={2} sx={{ p: 3 }}>
          <Stack direction='row' spacing={2} justifyContent={'space-between'}>
            <Stack sx={{ maxWidth: '50%' }}>
              <Typography variant='body6'>
                {dictionary.exercisedFrom}
              </Typography>
              <Typography variant='body2'>
                {format(new Date(data.nextEntitleDate), shortDateFormat)}
              </Typography>
            </Stack>
            {data.lastRefStartDate && (
              <Stack>
                <Typography variant='body6'>
                  {dictionary.previousExercised}
                </Typography>
                <Typography variant='body2'>
                  {format(new Date(data.lastRefStartDate), shortDateFormat)}
                </Typography>
              </Stack>
            )}
          </Stack>
          {isLoading ? (
            <Stack
              height={'25px'}
              justifyContent={'center'}
              alignItems={'end'}
              width={'33%'}
            >
              {loader}
            </Stack>
          ) : (
            data.medicalReferralId && (
              <Stack
                flexDirection={'row'}
                mb={1}
                alignItems={'center'}
                sx={{ cursor: 'pointer', maxWidth: '100%' }}
                component={'a'}
                tabIndex={0}
                onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                  download(e)
                }
              >
                <Avatar
                  src={Download2}
                  alt={''}
                  sx={{
                    width: '20px',
                    height: '20px',
                    mr: 1,
                    whiteSpace: 'nowrap',
                  }}
                />
                <Typography variant='body5' color='primary.main'>
                  {dictionary.downloadPreviousReference}
                </Typography>
              </Stack>
            )
          )}
        </Stack>
        <OneOrTwoButtons
          cardVariant
          width={isDesktop ? 'fit-content' : '100%'}
          leftButton={
            data.filterLabel
              ? {
                  label: dictionary.requestReference,
                  icon: LeftArrow,
                  onClick: () =>
                    data.shikumSubTypeId !== undefined
                      ? navigate(
                          `/universalRequest/1/2/${data.shikumSubTypeId}`
                        )
                      : navigate('/universalRequest/1/2'),
                }
              : undefined
          }
          rightButton={
            data.additionalInfoLink
              ? {
                  label: dictionary.moreInfo,
                  ariaLabel: `${dictionary.moreInfoAbout} ${data.description}`,
                  icon: ExternalLink,
                  onClick: () => {
                    navigateToAdditionalInfoLink();
                  },
                }
              : undefined
          }
        />
      </Grid>
    </>
  );
};
